/* World renderer. Canvas 2D at world-pixel resolution, scaled up by an integer factor with smoothing off.
   Per frame: copy the visible rows of cached chunk colours into one ImageData (viewport culling, chunk
   caching), then draw machinery, robots (sprite sheet from art/robots/build.py), particles and an additive light pass. */
import { W, CH, SURF, X0, CC_X, HEAP_X0, HEAP_W, ORE_BY_ID, CHAMBER_TYPES, M_PER_PX } from '../shared/config';
import { NS_DONE, NS_CHAMBER, NS_EDGE, F_BUILT, F_SUPPORT, F_SHAFT, chamberRx, GNode } from '../shared/graph';
import { S, CRobot } from './state';
import { cells, CELL, pip, MK2_SKILL, spritesReady } from './sprites';
import { isMine } from './launch';
import { hex, mix, css } from './world';

let cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D, out: ImageData | null = null;
let glow: HTMLCanvasElement;
export const view = { vw: 0, vh: 0, x0: 0, y0: 0, s: 2 };

export function initRender(canvas: HTMLCanvasElement) {
  cv = canvas; ctx = cv.getContext('2d', { alpha: false })!;
  glow = document.createElement('canvas'); glow.width = glow.height = 64;
  const g = glow.getContext('2d')!, gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(.35, 'rgba(255,255,255,.35)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
}
const BG = hex('#0b0c0e');
const ctypeOf = (n: GNode) => (n.type ? CHAMBER_TYPES[n.type - 1] : null);

export function layoutCanvas(host: HTMLElement) {
  const s = S.cam.s, vw = Math.ceil(host.clientWidth / s) + 1, vh = Math.ceil(host.clientHeight / s) + 1;
  if (vw !== view.vw || vh !== view.vh || s !== view.s) {
    view.vw = vw; view.vh = vh; view.s = s;
    cv.width = vw; cv.height = vh; cv.style.width = vw * s + 'px'; cv.style.height = vh * s + 'px';
    out = new ImageData(vw, vh); ctx.imageSmoothingEnabled = false;
  }
}

let genBudget = 0;
export function render(now: number) {
  if (!out) return;
  const { vw, vh, s } = view, w = S.world;
  /* centre the world when it is narrower than the viewport */
  const x0 = vw > W ? -Math.floor((vw - W) / 2) : Math.floor(S.cam.x), y0 = Math.floor(S.cam.y);
  view.x0 = x0; view.y0 = y0;
  cv.style.transform = `translate(${vw > W ? 0 : -(S.cam.x - x0) * s}px, ${-(S.cam.y - y0) * s}px)`;
  const d = out.data; genBudget = 2;
  const xa = Math.max(0, x0), xb = Math.min(W, x0 + vw);
  for (let r = 0; r < vh; r++) {
    const y = y0 + r, row = r * vw * 4;
    if (y < 0 || xa >= xb) { fillRow(d, row, 0, vw); continue; }
    const k = Math.floor(y / CH);
    let c = w.chunks.get(k);
    if (!c && genBudget-- > 0) c = w.chunk(k);
    if (!c) { fillRow(d, row, 0, vw, [16, 16, 18]); continue; }
    c.used = now;
    if (x0 < 0) fillRow(d, row, 0, -x0);
    const src = ((y % CH) * W + xa) * 4;
    d.set(c.rgba.subarray(src, src + (xb - xa) * 4), row + (xa - x0) * 4);
    if (x0 + vw > W) fillRow(d, row, W - x0, vw);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.putImageData(out, 0, 0);
  ctx.setTransform(1, 0, 0, 1, -x0, -y0);
  const t = now / 1000, y1 = y0 + vh, inV = (y: number, m = 20) => y > y0 - m && y < y1 + m;

  if (y0 < SURF + 10) drawSurface(t);
  drawGuides(x0, y0, vw, vh);
  const lights: [number, number, number, string, number][] = [];
  /* tunnels: lamps, supports, spoil piles, active faces */
  for (const n of w.nodes) {
    if (!n || !inV(n.y)) continue;
    if (n.state === NS_DONE && !n.type && n.id > 1 && n.id % 3 === 0) {
      const ly = ceilingY(n.x, n.y);
      ctx.fillStyle = '#e8c27a'; ctx.fillRect(Math.round(n.x), ly, 1, 1);
      lights.push([n.x, ly + 1, 9, '255,196,120', .16]);
    }
    if (n.flags & F_SUPPORT && !n.type) drawSupport(n);
    if (n.flags & F_SHAFT && n.state === NS_DONE) { ctx.fillStyle = 'rgba(200,145,58,.22)'; ctx.fillRect(Math.round(n.x) - 3, Math.round(n.y), 1, 1); ctx.fillRect(Math.round(n.x) + 3, Math.round(n.y), 1, 1); }
    if (n.cargo > 0) drawPile(n);
    if (n.state === NS_EDGE && n.carved > 1) {
      const h = n.pts[Math.min(n.pts.length - 1, n.prog)];
      lights.push([h[0], h[1], 14, '235,235,225', .22 + .06 * Math.sin(t * 9 + n.id)]);
    }
  }
  for (const n of w.nodes) {
    if (!n || !n.type || !inV(n.y, 30) || n.cp <= 0) continue;
    drawChamber(n, t, lights);
  }
  drawDeposits();
  /* robots */
  for (const r of S.robots.values()) {
    if (!r.seen || !inV(r.y, 10)) continue;
    drawRobot(r, t, lights);
  }
  /* particles */
  for (const p of S.parts) { ctx.globalAlpha = Math.max(0, p.life / p.max); ctx.fillStyle = p.col; ctx.fillRect(Math.round(p.x), Math.round(p.y), 1, 1); }
  ctx.globalAlpha = 1;
  /* additive light pass */
  ctx.globalCompositeOperation = 'lighter';
  for (const [x, y, rad, col, a] of lights) {
    ctx.globalAlpha = a; ctx.drawImage(tint(col), x - rad, y - rad, rad * 2, rad * 2);
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  drawSelection(t);
}

function fillRow(d: Uint8ClampedArray, row: number, a: number, b: number, c = BG as number[]) {
  for (let x = a; x < b; x++) { const i = row + x * 4; d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2]; d[i + 3] = 255; }
}
const tints = new Map<string, HTMLCanvasElement>();
function tint(col: string) {
  let c = tints.get(col); if (c) return c;
  c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!; g.drawImage(glow, 0, 0); g.globalCompositeOperation = 'source-in'; g.fillStyle = `rgb(${col})`; g.fillRect(0, 0, 64, 64);
  tints.set(col, c); return c;
}
function ceilingY(x: number, y: number) { let cy = Math.round(y); for (let k = 0; k < 6 && S.world.dugAt(Math.round(x), cy - 1); k++) cy--; return cy; }
function floorY(x: number, y: number) { let fy = Math.round(y); for (let k = 0; k < 8 && S.world.dugAt(Math.round(x), fy + 1); k++) fy++; return fy; }

/* faint depth grid every 50 m in the side margins, for scale */
function drawGuides(x0: number, y0: number, vw: number, vh: number) {
  if (x0 >= 0) return;
  ctx.fillStyle = 'rgba(217,212,199,.06)';
  const step = 100 / M_PER_PX;
  for (let y = Math.ceil((y0 - SURF) / step) * step + SURF; y < y0 + vh; y += step) { ctx.fillRect(x0, y, -x0 - 2, 1); ctx.fillRect(W + 2, y, vw + x0 - W, 1); }
}

/* ---------- surface ---------- */
function drawSurface(t: number) {
  const surf = S.world.terrain.surf, sy = surf[X0];
  /* tailings heap */
  for (let x = HEAP_X0; x < HEAP_X0 + HEAP_W; x++) {
    const h = Math.floor(S.world.heap[x] || 0); if (!h) continue;
    for (let j = 1; j <= h; j++) { ctx.fillStyle = j === h ? '#8d8676' : (x * 7 + j * 13) % 5 === 0 ? '#6e675a' : '#5e584d'; ctx.fillRect(x, surf[x] - j, 1, 1); }
  }
  /* command center: low concrete block, lit windows, antenna */
  const cx = CC_X - 22, cy = surf[CC_X] - 16;
  ctx.fillStyle = '#2a2b2f'; ctx.fillRect(cx, cy, 44, 16);
  ctx.fillStyle = '#3a3b40'; ctx.fillRect(cx, cy, 44, 2); ctx.fillRect(cx + 30, cy - 6, 12, 6);
  ctx.fillStyle = '#1c1d20'; ctx.fillRect(cx + 4, cy + 8, 9, 8);
  for (let i = 0; i < 5; i++) { ctx.fillStyle = (Math.floor(t * .3 + i * 1.7) % 7) ? '#c8913a' : '#5a4422'; ctx.fillRect(cx + 16 + i * 5, cy + 5, 3, 2); }
  ctx.fillStyle = '#54575c'; ctx.fillRect(cx + 36, cy - 18, 1, 12); ctx.fillRect(cx + 34, cy - 16, 5, 1);
  ctx.fillStyle = Math.floor(t * 1.2) % 2 ? '#c45a3c' : '#3a1e18'; ctx.fillRect(cx + 36, cy - 19, 1, 1);
  ctx.fillStyle = '#d9d4c7'; ctx.font = '5px monospace';
  /* charging docks in front of the CC */
  for (let s = 0; s < 3; s++) { const x = CC_X - 14 + s * 14; ctx.fillStyle = '#3b3d42'; ctx.fillRect(x - 3, surf[x], 7, 1); ctx.fillStyle = '#7fa37a'; ctx.fillRect(x, surf[x], 1, 1); }
  /* headframe over the shaft: lattice legs, sheave wheel turning while units ride the shaft */
  const hx = X0, top = sy - 34;
  ctx.fillStyle = '#4a4c51';
  for (let j = 0; j < 34; j++) { const sp = Math.round(9 - j * .18); ctx.fillRect(hx - sp, sy - j, 1, 1); ctx.fillRect(hx + sp, sy - j, 1, 1); if (j % 6 === 0) ctx.fillRect(hx - sp, sy - j, sp * 2 + 1, 1); }
  for (let j = 2; j < 34; j += 6) for (let k = 0; k < 5; k++) { const sp = 9 - (j + k) * .18; ctx.fillRect(Math.round(hx - sp + k * sp * .4), sy - j - k, 1, 1); }
  ctx.fillStyle = '#6a6d73'; ctx.fillRect(hx - 6, top - 1, 13, 2);
  const ang = t * 1.6;
  for (let k = 0; k < 8; k++) { const a = ang + k * Math.PI / 4; ctx.fillStyle = k % 2 ? '#8a8d93' : '#c8913a'; ctx.fillRect(Math.round(hx + Math.cos(a) * 3.5), Math.round(top - 4 + Math.sin(a) * 3.5), 1, 1); }
  ctx.fillStyle = '#d9d4c7'; ctx.fillRect(hx, top - 4, 1, 1);
  ctx.fillStyle = 'rgba(217,212,199,.35)'; ctx.fillRect(hx, top - 1, 1, sy - top + 1);
  ctx.fillStyle = Math.floor(t * 2) % 2 ? '#c45a3c' : '#2a1410'; ctx.fillRect(hx, top - 9, 1, 1);
  /* power line to the CC */
  ctx.fillStyle = '#2f3135'; for (let x = cx + 44; x < hx - 9; x++) ctx.fillRect(x, top + 12 + Math.round(Math.sin((x - cx) / 10) * 1), 1, 1);
}

/* ---------- tunnels ---------- */
function drawSupport(n: GNode) {
  const x = Math.round(n.x), cy = ceilingY(n.x, n.y), fy = floorY(n.x, n.y);
  ctx.fillStyle = '#6b5a44'; ctx.fillRect(x - 3, cy, 1, fy - cy + 1); ctx.fillRect(x + 3, cy, 1, fy - cy + 1);
  ctx.fillStyle = '#8a7457'; ctx.fillRect(x - 3, cy, 7, 1);
}
function drawPile(n: GNode) {
  const h = n.state === NS_EDGE ? n.pts[Math.min(n.pts.length - 1, n.prog)] : [n.x, n.y];
  const x = Math.round(h[0]), fy = floorY(h[0], h[1]);
  const k = Math.min(n.cargo, 10);
  for (let i = 0; i < k; i++) { const px = x - 2 + (i * 3) % 5, py = fy - Math.floor(i / 5); ctx.fillStyle = i % 3 ? '#5c5548' : '#7a6f5d'; ctx.fillRect(px, py, 1, 1); }
}

/* ---------- chambers ---------- */
function drawChamber(n: GNode, t: number, lights: [number, number, number, string, number][]) {
  const type = ctypeOf(n)!; if (type === 'stope') return;
  const cx = Math.round(n.x), cy = Math.round(n.y), rx = chamberRx(n.r), ry = n.r, fy = cy + Math.floor(ry * .7), built = !!(n.flags & F_BUILT);
  if (n.state !== NS_DONE) return;
  if (!built) {
    /* scaffolding and a progress bar while engineers build */
    ctx.fillStyle = 'rgba(200,145,58,.55)';
    for (let x = Math.ceil(cx - rx * .7); x <= cx + rx * .7; x += 4) ctx.fillRect(x, cy - Math.floor(ry * .6), 1, fy - cy + Math.floor(ry * .6) + 1);
    for (let y = cy - Math.floor(ry * .6); y <= fy; y += 3) ctx.fillRect(Math.ceil(cx - rx * .7), y, Math.floor(rx * 1.4), 1);
    ctx.fillStyle = '#26282c'; ctx.fillRect(cx - 8, cy - ry - 4, 17, 2);
    ctx.fillStyle = '#c8913a'; ctx.fillRect(cx - 8, cy - ry - 4, Math.round(17 * n.build), 2);
    if (Math.floor(t * 6 + n.id) % 4 === 0) { ctx.fillStyle = '#fff1c4'; ctx.fillRect(cx + ((n.id * 7) % 9) - 4, fy - 2, 1, 1); lights.push([cx, fy - 2, 8, '255,220,150', .25]); }
    return;
  }
  /* deck plating */
  ctx.fillStyle = '#34363b'; ctx.fillRect(Math.round(cx - rx * .85), fy, Math.round(rx * 1.7), 1);
  ctx.fillStyle = '#4a4c51'; for (let x = Math.round(cx - rx * .85); x < cx + rx * .85; x += 3) ctx.fillRect(x, fy, 1, 1);
  lights.push([cx, cy - 2, rx + 6, '255,190,120', .2]);
  switch (type) {
    case 'charging': {
      for (let s = 0; s < 4; s++) {
        const x = Math.round(n.x - rx * .6 + s * (rx * 1.2 / 3));
        const occ = [...S.robots.values()].some(r => r.seen && r.st === 5 && Math.abs(r.x - x) < 1.5 && Math.abs(r.y - (n.y + n.r * .55)) < 2);
        ctx.fillStyle = '#26282c'; ctx.fillRect(x - 2, fy - 1, 5, 2);
        ctx.fillStyle = occ ? (Math.floor(t * 3 + s) % 2 ? '#9fe08f' : '#4f7a46') : '#c8913a'; ctx.fillRect(x, fy - 1, 1, 1);
        ctx.fillStyle = '#54575c'; ctx.fillRect(x, cy - Math.floor(ry * .7), 1, 3);
        if (occ) lights.push([x, fy - 3, 6, '140,230,130', .3]);
      }
      break;
    }
    case 'storage': {
      const raw = S.stats?.rawOre || {}, ids = Object.keys(raw).filter(k => raw[k] > 0);
      const fill = Math.min(1, (S.stats?.rawTotal || 0) / Math.max(100, S.stats?.storageCap || 400) * 4 + .25);
      const cols = Math.floor(rx * 1.4 / 4);
      for (let i = 0; i < cols; i++) for (let j = 0; j < 3; j++) {
        if ((i * 3 + j) / (cols * 3) > fill) continue;
        const id = ids.length ? +ids[(i + j * 2 + n.id) % ids.length] : 17, o = ORE_BY_ID[id];
        const x = Math.round(cx - rx * .7 + i * 4), y = fy - 3 - j * 3;
        ctx.fillStyle = '#26282c'; ctx.fillRect(x, y, 3, 3); ctx.fillStyle = o ? o.col : '#7d4f42'; ctx.fillRect(x + 1, y + 1, 1, 1);
      }
      break;
    }
    case 'processing': {
      ctx.fillStyle = '#45474c'; ctx.fillRect(cx - 9, fy - 6, 7, 6); ctx.fillRect(cx - 8, fy - 9, 5, 3);
      const a = t * 4;
      for (let k = 0; k < 6; k++) { ctx.fillStyle = k % 2 ? '#c9a23c' : '#7d8086'; ctx.fillRect(Math.round(cx + 4 + Math.cos(a + k) * 3), Math.round(fy - 4 + Math.sin(a + k) * 3), 1, 1); }
      ctx.fillStyle = '#26282c'; ctx.fillRect(cx - 2, fy - 1, 12, 1);
      for (let k = 0; k < 4; k++) { ctx.fillStyle = '#b5703f'; ctx.fillRect(cx - 2 + Math.floor((t * 6 + k * 3) % 12), fy - 2, 1, 1); }
      lights.push([cx + 4, fy - 4, 8, '230,180,80', .2]);
      break;
    }
    case 'research': {
      for (let i = -1; i <= 1; i++) {
        const x = cx + i * 7; ctx.fillStyle = '#2d3036'; ctx.fillRect(x - 2, fy - 4, 5, 4);
        for (let k = 0; k < 3; k++) { ctx.fillStyle = (Math.floor(t * 4 + k * 3 + i * 5) % 4) ? '#6f9c9a' : '#c8913a'; ctx.fillRect(x - 1 + k, fy - 3, 1, 1); }
      }
      const sx = Math.round(cx + Math.sin(t * 1.4) * rx * .7);
      ctx.fillStyle = 'rgba(127,214,207,.35)'; ctx.fillRect(sx, cy - Math.floor(ry * .6), 1, fy - cy + Math.floor(ry * .6) - 4);
      lights.push([cx, fy - 4, 10, '120,210,200', .22]);
      break;
    }
    case 'power': {
      const pulse = .5 + .5 * Math.sin(t * 3 + n.id);
      ctx.fillStyle = '#3a3c41'; ctx.fillRect(cx - 5, fy - 8, 11, 8);
      ctx.fillStyle = mixc('#5a2216', '#ff8a4c', pulse); ctx.fillRect(cx - 2, fy - 6, 5, 4);
      ctx.fillStyle = '#54575c'; ctx.fillRect(cx - 5, fy - 9, 11, 1); ctx.fillRect(cx + 7, fy - 5, 4, 5);
      lights.push([cx, fy - 4, 12, '255,120,60', .22 + .18 * pulse]);
      break;
    }
    case 'assembly': {
      const top = cy - Math.floor(ry * .6);
      ctx.fillStyle = '#54575c'; ctx.fillRect(Math.round(cx - rx * .75), top, Math.round(rx * 1.5), 1);
      const tx = Math.round(cx + Math.sin(t * .9 + n.id) * rx * .6);
      ctx.fillStyle = '#c8913a'; ctx.fillRect(tx - 1, top + 1, 3, 1); ctx.fillStyle = '#7d8086'; ctx.fillRect(tx, top + 2, 1, fy - top - 5);
      ctx.fillStyle = Math.floor(t * 2) % 2 ? '#d9d4c7' : '#7d8086'; ctx.fillRect(cx - 2, fy - 3, 5, 3);
      if (Math.floor(t * 8) % 3 === 0) { ctx.fillStyle = '#fff1c4'; ctx.fillRect(tx, fy - 4, 1, 1); lights.push([tx, fy - 4, 6, '255,230,170', .35]); }
      break;
    }
  }
  drawDoor(n, rx, t);
}
function mixc(a: string, b: string, k: number) { return css(mix(hex(a), hex(b), k)); }
/* sliding blast door where the access tunnel meets the chamber */
function drawDoor(n: GNode, rx: number, t: number) {
  const p = S.world.nodes[n.parent]; if (!p) return;
  const dir = Math.sign(p.x - n.x) || 1, dx = Math.round(n.x + dir * (rx - .5)), dy = Math.round(n.y);
  let near = false;
  for (const r of S.robots.values()) if (r.seen && Math.abs(r.x - dx) < 9 && Math.abs(r.y - dy) < 6) { near = true; break; }
  const cur = S.doors.get(n.id) ?? 0, open = Math.max(0, Math.min(1, cur + (near ? .12 : -.05)));
  S.doors.set(n.id, open);
  const top = ceilingY(dx, dy), bot = floorY(dx, dy), h = bot - top + 1, shut = Math.round(h * (1 - open));
  ctx.fillStyle = '#26282c'; ctx.fillRect(dx - dir, top - 1, 1, h + 1);
  if (shut > 0) { ctx.fillStyle = '#6a6d73'; ctx.fillRect(dx, top, 1, shut); ctx.fillStyle = '#c8913a'; ctx.fillRect(dx, top + shut - 1, 1, 1); }
}

/* ---------- surveyed deposits ---------- */
function drawDeposits() {
  const t = S.world.terrain; if (!t) return;
  const k0 = Math.floor(view.y0 / CH) - 1, k1 = Math.floor((view.y0 + view.vh) / CH) + 1;
  for (let k = Math.max(0, k0); k <= k1; k++) for (const d of t.deposits(k)) {
    if (!S.known.has(d.id) || S.breached.has(d.id)) continue;
    const x0 = Math.round(d.x - d.rx - 2), x1 = Math.round(d.x + d.rx + 2), y0 = Math.round(d.y - d.ry - 2), y1 = Math.round(d.y + d.ry + 2);
    ctx.fillStyle = 'rgba(127,214,207,.7)';
    for (const [x, y, sx, sy] of [[x0, y0, 1, 1], [x1, y0, -1, 1], [x0, y1, 1, -1], [x1, y1, -1, -1]]) { ctx.fillRect(x, y, 3 * sx, 1); ctx.fillRect(x, y, 1, 3 * sy); }
  }
}

/* ---------- robots ---------- */
const CARGO_RUBBLE = '#7a6f5d';
/* Skills show on the units themselves:
   Mk II hull per class (excav, logistics, detect, struct, deep) comes from the sprite sheet;
   battery: status pip turns into a glowing regen cell; path: cyan nav beacon;
   parallel: every beacon blinks in sync; expand: units built after it carry a white Mk stripe. */
function drawRobot(r: CRobot, t: number, lights: [number, number, number, string, number][]) {
  if (!spritesReady) return;
  const sk = S.stats?.skills || {}, has = (k: string) => sk[k] !== undefined;
  const working = r.st === 3, mk = has(MK2_SKILL[r.cls]) ? 1 : 0;
  const f = working ? 4 + (Math.floor(t * 9 + r.id) & 3) : (Math.floor(r.step) & 3);
  const cell = cells[r.cls]?.[mk]?.[f]?.[r.left ? 1 : 0]; if (!cell) return;
  const x = Math.round(r.x - CELL.w / 2), y = Math.round(r.y + 3 - (CELL.h - 1));
  ctx.drawImage(cell.c, x, y);
  if (cell.cargo.length) {
    ctx.fillStyle = r.carry ? (r.carry > 1 && ORE_BY_ID[r.carry] ? ORE_BY_ID[r.carry].col : CARGO_RUBBLE) : '#202125';
    for (const [cx, cy] of cell.cargo) ctx.fillRect(x + cx, y + cy, 1, 1);
  }
  /* status pip: battery level; Battery Optimization adds a regen cell */
  const p = pip[r.cls], px = x + (r.left ? CELL.w - 1 - p[0] : p[0]), py = y + p[1];
  const batCol = r.st === 5 ? (Math.floor(t * 3 + r.id) % 2 ? '#9fe08f' : '#3c5c36') : r.bat < 20 ? (Math.floor(t * 2) % 2 ? '#c45a3c' : '#3a1e18') : r.bat < 50 ? '#c8913a' : '#7fa37a';
  ctx.fillStyle = batCol; ctx.fillRect(px, py, 1, 1);
  if (has('battery')) { ctx.fillStyle = '#9fe0d6'; ctx.fillRect(px + (r.left ? 1 : -1), py, 1, 1); if (r.st === 5) lights.push([px, py, 4, '160,230,214', .35]); }
  /* Advanced Pathfinding: nav beacon; Parallel Operations synchronises the whole fleet */
  if (has('path') && r.st !== 6) {
    const phase = has('parallel') ? t * 1.25 : t * 1.25 + r.id * .37;
    if (Math.floor(phase * 2) % 2 === 0) { ctx.fillStyle = '#7fd6cf'; ctx.fillRect(px, py - 1, 1, 1); if (has('parallel')) lights.push([px, py - 1, 4, '127,214,207', .3]); }
  }
  /* Autonomous Expansion: second-generation units carry a white stripe */
  if (has('expand') && r.born >= sk.expand) { ctx.fillStyle = '#f2efe6'; ctx.fillRect(x + 3, y + 6, CELL.w - 7, 1); }
  if (r.st === 6) { ctx.fillStyle = Math.floor(t * 10) % 2 ? '#7fd6cf' : '#c45a3c'; ctx.fillRect(px, py - 1, 1, 1); }
  /* launched agents fly a pennant: green for yours, amber for everyone else's */
  if (r.owner) {
    const mine = isMine(r.owner), fx = x + (r.left ? CELL.w - 3 : 2), top = y - 3;
    ctx.fillStyle = '#9a9c94'; ctx.fillRect(fx, top, 1, 4);
    ctx.fillStyle = mine ? '#9fe08f' : '#e6b766'; ctx.fillRect(fx + (r.left ? -2 : 1), top, 2, 1); ctx.fillRect(fx + (r.left ? -1 : 1), top + 1, 1, 1);
    if (mine) lights.push([fx, top, 6, '159,224,143', .35]);
  }
  /* headlamp */
  if (r.y > SURF) lights.push([r.x + (r.left ? -5 : 5), r.y, 5, '255,230,180', .16]);
  if (working && (r.cls === 0 || r.cls === 4) && Math.random() < .35) {
    const hx = r.x + (r.left ? -6 : 6);
    S.parts.push({ x: hx, y: r.y, vx: (Math.random() - .5) * 18 + (r.left ? -6 : 6), vy: -Math.random() * 12, life: .35, max: .35, col: mk ? (Math.random() < .5 ? '#9fe0d6' : '#f2efe6') : (Math.random() < .5 ? '#ffd27a' : '#f2efe6'), g: 30 });
    lights.push([hx, r.y, 7, mk ? '160,230,214' : '255,210,140', .25]);
  }
}

/* ---------- selection ---------- */
function drawSelection(t: number) {
  const sel = S.sel; if (!sel) return;
  let x = 0, y = 0, rw = 6, rh = 5;
  if (sel.kind === 'robot') { const r = S.robots.get(sel.id); if (!r || !r.seen) return; x = r.x; y = r.y - 1; rw = 8; rh = 6; }
  else if (sel.kind === 'chamber') { const n = S.world.nodes[sel.id]; if (!n) return; x = n.x; y = n.y; rw = chamberRx(n.r) + 3; rh = n.r + 3; }
  else if (sel.kind === 'deposit') { const d = depositById(sel.id); if (!d) return; x = d.x; y = d.y; rw = d.rx + 4; rh = d.ry + 4; }
  else { x = sel.x; y = sel.y; rw = 3; rh = 3; }
  const a = .6 + .4 * Math.sin(t * 5);
  ctx.fillStyle = `rgba(232,180,90,${a})`;
  const x0 = Math.round(x - rw), x1 = Math.round(x + rw), y0 = Math.round(y - rh), y1 = Math.round(y + rh);
  for (const [px, py, sx, sy] of [[x0, y0, 1, 1], [x1, y0, -1, 1], [x0, y1, 1, -1], [x1, y1, -1, -1]]) { ctx.fillRect(px, py, 2 * sx, 1); ctx.fillRect(px, py, 1, 2 * sy); }
}
export function depositById(id: number) { const k = Math.floor(id / 8); return S.world.terrain?.deposits(k).find(d => d.id === id) || null; }

/* ---------- depth rail (minimap) ---------- */
export function drawRail(rc: HTMLCanvasElement) {
  const g = rc.getContext('2d')!, w = rc.width, h = rc.height, deep = Math.max(SURF + 200, S.world.deepestY() + 120);
  g.clearRect(0, 0, w, h);
  g.fillStyle = '#121316'; g.fillRect(0, 0, w, h);
  const sx = w / W, sy = h / deep;
  g.fillStyle = '#26282c'; g.fillRect(0, SURF * sy, w, 1);
  g.strokeStyle = '#7a6f5d'; g.lineWidth = 1; g.beginPath();
  for (const n of S.world.nodes) { if (!n || n.parent < 0 || n.carved < 2) continue; const p = S.world.nodes[n.parent]; g.moveTo(p.x * sx, p.y * sy); g.lineTo(n.x * sx, n.y * sy); }
  g.stroke();
  for (const n of S.world.nodes) if (n && n.type && n.state === NS_DONE) { g.fillStyle = n.flags & F_BUILT ? '#c8913a' : '#5a4422'; g.fillRect(n.x * sx - 1, n.y * sy - 1, 2, 2); }
  for (const n of S.world.nodes) if (n && n.state === NS_EDGE) { g.fillStyle = '#f2efe6'; g.fillRect(n.x * sx, n.y * sy, 1, 1); }
  g.strokeStyle = 'rgba(232,180,90,.9)'; g.strokeRect(.5 + Math.max(0, view.x0) * sx, .5 + view.y0 * sy, Math.min(W, view.vw) * sx - 1, Math.max(2, view.vh * sy));
  return deep;
}
